import React, { useState,useEffect, useRef }  from 'react';
import { GetRequest, PostRequest } from 'components/common/index.js';

const PaymentForm = ({ subscription, setSubscription, setCurrencyName, setMode }) => {
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const cardElementRef = useRef();
  const paymentFormRef = useRef();

  const setUp = () => {
    // Setup function kept for potential future use
  }

  useEffect( () => {
    let listen = 'show_drawing.currency_changed';
    document.addEventListener(listen, fetchSubscription);
    return () => { document.removeEventListener(listen, fetchSubscription);}
  }, [])

  useEffect( () => {
    $("#preview_promo_modal_content_container").css("height", "unset");
  }, [])

  useEffect(setUp, []);

  useEffect( () => {
    trackEvent('js_selected_subscription', {
      item: subscription,
    });

  }, [subscription]);

  const appearance = {
    theme: 'stripe',

    variables: {
      colorPrimary: '#eb5424',
      colorBackground: '#ffffff',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    }
  };

  const fetchSubscription = () => {
    GetRequest({
      url: '/charge/subscriptions',
      callback: (data) => {
        // Re-fetch the same plan after a currency change. The tiers key the JSON
        // by their checkout_type (e.g. monthly_business); bronze keys it under
        // monthly_subscription / annual_subscription, so map those two.
        let key = subscription.checkout_type || subscription.type;
        if(key == 'monthly')  { key = 'monthly_subscription'}
        if(key == 'annually') { key = 'annual_subscription'}

        let newSubscription = data[key]
        setSubscription( _prev => newSubscription)
        setCurrencyName( _prev => data.currency_name);
      }
    })
  }

  const setPayCTA = (value) => {
    setPayWithCardCTA( () => value)
  }

  // Recurring plans bake the amount + cadence into the CTA ("Subscribe — 599.00
  // USD/year"), matching the "Get Started — …" button that opened the modal;
  // one-off credit purchases keep the neutral "Submit Payment" wording.
  const defaultPayCTA = () => {
    if(!subscription.recurring) { return t('sign_up.submit_payment'); }
    const key = subscription.type == 'annually' ? 'sign_up.subscribe_price_annual' : 'sign_up.subscribe_price_monthly';
    return t(key).replace('%{price}', subscription.price_formatted);
  };

  const [payWithCardCTA, setPayWithCardCTA] = useState(defaultPayCTA());

  // Re-label the pay button whenever the plan changes (currency change, or the
  // monthly/annual switch link) so its amount + cadence stay in sync.
  useEffect(() => { setPayWithCardCTA(defaultPayCTA()); }, [subscription]);

  const setPayButtonDisabled = (value) => {
    setPayWithCardDisabled( () => value)
  }

  const [payWithCardDisabled, setPayWithCardDisabled] = useState('disabled');

  const setCardErrorMessage = (value) => {
    setCardErrorMessageDefault( () => value)
  }
  const [cardErrorMessageDefault, setCardErrorMessageDefault] = useState('');

  useEffect(() => {
    if(cardErrorMessageDefault != '') {
      trackEvent('js_stripe_card_error_message', {
        message: cardErrorMessageDefault,
        stripeReturnURL: window.stripeReturnURL,
      })
    }

  }, [cardErrorMessageDefault])

  const [loading, setLoading] = useState(true);
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentCoupon, setPaymentIntentCoupon] = useState('');

  var elements = useRef()
  var paymentElement = useRef()

  const stripe = Stripe(window.stripePublishableKey);

  const paymentElementOptions = { layout: "tabs", defaultValues: { billingDetails: { email: window.currentUserEmail } } };

  const onPaymentElementReady = () => {
    setLoading( () => false );
    paymentElement.current.focus();
    setPayButtonDisabled('');
  }


  const processPaymentIntentResult = (paymentIntentId, callback) => {
    PostRequest({
      url: `/charges/process_payment_intent_result/${paymentIntentId}`,
      data: {},
      callback: callback,
    });
  }

  const processConfirmResult = (result) => {
    if(result?.paymentIntent?.status) {
      setPaymentIntentId( () => result.paymentIntent.id );
      processPaymentIntentResult(result.paymentIntent.id, (data) => {
        setCardErrorMessage('');
        // Multi-seat buyers go straight to their team's user-management page.
        // processPaymentIntentResult ran the purchase job synchronously above, so
        // the reseller account already exists.
        if (subscription.seats > 1) {
          window.location.href = '/my-reseller-account';
          return;
        }
        setMode('payment_success');
      })
      setSubmitInProgress( () => false );
      return;
    }

    if(result.error != undefined) {
      setCardErrorMessage(result.error.message);
      paymentElement.current.clear();

      setPayButtonDisabled('');
      setPayCTA(defaultPayCTA());
      setSubmitInProgress( () => false );
      return;
    }

    setSubmitInProgress( () => false );
  }

  const createPaymentIntent = () => {
    setPayWithCardDisabled(() => 'disabled');
    setLoading(() => true );

    PostRequest({
      url: "/charges/create_payment_intent/",
      data: {
        subscription_type: subscription.checkout_type || subscription.type,
        currency: subscription.currency,
        code: subscription.coupon_code,
      },
      callback: (data) => {
        elements.current = stripe.elements({
          appearance: appearance,
          clientSecret: data.clientSecret,
        });

        setPaymentIntentId( () => data.payment_intent_id);
        setClientSecret( () => data.clientSecret);
        setPaymentIntentCoupon( () => data.payment_intent_coupon);
        paymentElement.current = elements.current.create("payment", paymentElementOptions);
        paymentElement.current.mount(cardElementRef.current);
        paymentElement.current.addEventListener('ready', onPaymentElementReady);
        paymentFormRef.current.addEventListener('submit', handleSubmit);
        setLoading( () => false );
        setCardErrorMessage( '' );
      },
      403: (data) => {
        setCardErrorMessage(data.error)
        setLoading( () => false );
      }
    });
  }

  useEffect(createPaymentIntent, [subscription])

  const handleSubmit = (e) => {
    e.preventDefault();
    if(submitInProgress) { return }

    setPayButtonDisabled('disabled');
    setPayCTA(t('sign_up.please_wait'));
    setCardErrorMessage('');

    setSubmitInProgress( () => true )
    stripe.confirmPayment({
      elements: elements.current,
      confirmParams: {
        return_url: window.stripeReturnURL,
        receipt_email: window.currentUserEmail,
      },
      redirect: 'if_required',
    }).then(processConfirmResult)
  }

  const loadingIcon = () => { return (<><img src={window.loading_icon} /> <br /> <br /></>) }

  const fetchElementUpdates = (data) => {
    if(elements.current == undefined) { return }

    elements.current.fetchUpdates()
      .then(function(result) {
        setPayButtonDisabled('');
        setPayCTA(defaultPayCTA());
    });
  }

  // Last-minute interval swap from inside the checkout modal: re-fetch the plans
  // and drop in the counterpart interval's plan. setSubscription then re-runs
  // createPaymentIntent (new Stripe intent) and re-prices the modal + button,
  // exactly as a currency change does. A multi-seat annual plan has no monthly
  // equivalent, so it lands on the single-seat monthly plan.
  const switchInterval = (e) => {
    e.preventDefault();
    const goAnnual = subscription.type != 'annually';
    GetRequest({
      url: '/charge/subscriptions',
      callback: (data) => {
        let key;
        if(subscription.checkout_type) {
          key = goAnnual
            ? subscription.checkout_type.replace(/^monthly_/, 'annually_')
            : subscription.checkout_type.replace(/^annually_/, 'monthly_').replace(/_for_\d+$/, '');
        } else {
          key = goAnnual ? 'annual_subscription' : 'monthly_subscription';
        }
        const next = data[key];
        if(next) { setSubscription(() => next); }
      }
    });
  }

  return (<>
    <div className="plan">
      <div className="plan-head">
        <div className="price" style={{"fontSize": "28px"}}>
          { t(`sign_up.${subscription.type}`) }
        </div>
      </div>

      <div className="plan-head">
        <div className="price" style={{"borderBottom": "0px", "paddingBottom": "0px"}}>
          {subscription.has_discount && <>
            <strike>
              <small>
                <font color="#cccccc"> {subscription.price_without_discount_formatted} </font>
              </small>
            </strike>
            &nbsp;
          </>}

          {subscription.price_formatted}
          {!subscription.recurring && <span> {t('sign_up.one_off')} </span> }
          {subscription.recurring && subscription.type == 'monthly' && <span>  {t('sign_up.per_month')}, {t('sign_up.auto_renew').toLowerCase()} </span> }
          {subscription.recurring && subscription.type == 'annually' && <span> {t('sign_up.per_year')}, {t('sign_up.auto_renew').toLowerCase()} </span> }
        </div>

        <span className="price" style={{"fontSize": "14px", "borderBottom": "0px", "paddingTop": "0px", "lineHeight": "1.4"}}>
          <span>
            {subscription.recurring ? (
              <span style={{'display': 'inline', "fontSize": "14px"}} dangerouslySetInnerHTML={{__html: t(subscription.type == 'annually' ? 'sign_up.recurring_charge_annually' : 'sign_up.recurring_charge_monthly').replace('%{today}', subscription.transaction_amount_formatted).replace('%{recurring}', subscription.price_formatted) }}></span>
            ) : (
              <span style={{'display': 'inline', "fontSize": "14px"}} dangerouslySetInnerHTML={{__html: t('sign_up.your_card_will_be_debited').replace('%{price}', subscription.transaction_amount_formatted) }}></span>
            )}
          </span>

          {subscription.has_discount && (
            <>
            <span style={{"display": "inline"}}> {t('sign_up.promo_code')} </span> {subscription.coupon_code}, &nbsp; <span style={{"display": "inline"}}> {t('sign_up.discount_off')} </span> {subscription.discount_formatted}
          </>)}

        </span>
      </div>


      <div className="all_body_cont" id="content-stripe">
        <form ref={paymentFormRef}>
          <div ref={cardElementRef} style={{ "height": "100%" }}></div>


          <br />

          {cardErrorMessageDefault != '' && <>
            <div id="card-errors" className="well card-errors-react">
              {cardErrorMessageDefault}
            </div>
          </>}

          {!loading && cardElementRef.current && ( <>
            {subscription.recurring && (
              <p style={{"fontSize": "12px", "color": "#777", "marginBottom": "8px"}} dangerouslySetInnerHTML={{__html: t('sign_up.recurring_terms_acknowledgement')}}></p>
            )}
            <div style={{"display": "flex", "alignItems": "center"}}>
              {/* Equal side spacers keep the pay button centred while the interval
                  switch link floats to the right edge, away from the main CTA. */}
              <div style={{"flex": "1"}}></div>
              <button className="stripe-button btn btn-default simple-btn" disabled={payWithCardDisabled}>
                {payWithCardCTA}
              </button>
              <div style={{"flex": "1", "textAlign": "right", "paddingLeft": "16px"}}>
                {subscription.recurring && (
                  <a href="#" onClick={switchInterval}>
                    {subscription.type == 'annually' ? t('sign_up.switch_to_monthly') : t('sign_up.switch_to_annual')}
                  </a>
                )}
              </div>
            </div>
            <br />
            <br />
          </>)}

          {loading && loadingIcon() }
        </form>
      </div>

    </div>
  </>)
}

export default PaymentForm;
